import _ from 'lodash'
import {createContext, FC, useCallback, useContext, useReducer} from 'react'

import {SNACKBAR_AUTO_DISMISS, SnackbarType} from '../constants'
import {Snackbar} from '../Snackbar'

type SnackbarContextType = {
  closeSnackbar: () => void
  showSnackbar: (
    text: string,
    autoHide: boolean,
    type?: SnackbarType,
    buttonText?: string,
    onClick?: () => void,
  ) => void
}

const SnackbarContext = createContext<SnackbarContextType>({
  closeSnackbar: _.identity,
  showSnackbar: _.identity,
})

export const useSnackbarContext = (): SnackbarContextType => useContext(SnackbarContext)

type State = {
  text: string
  type: SnackbarType
  isVisible: boolean
  buttonText?: string
  onClick?: () => void
}

enum Actions {
  HIDE = 'hide',
  SHOW = 'show',
}

const initialState = {
  text: '',
  type: SnackbarType.info,
  isVisible: false,
  buttonText: '',
  onClick: undefined,
} as State

const reducer = (state: State, action: {type: Actions; payload?: Partial<State>}): State => {
  switch (action.type) {
    case Actions.SHOW:
      return {
        ...state,
        text: action.payload?.text ?? state.text,
        type: action.payload?.type ?? state.type,
        isVisible: true,
        buttonText: action.payload?.buttonText ?? state.buttonText,
        onClick: action.payload?.onClick ?? state.onClick,
      }
    case Actions.HIDE:
      return {...state, isVisible: false}
    default:
      return state
  }
}

export type SnackbarProps = {
  text: string
  type: SnackbarType
  isVisible: boolean
  buttonText?: string
  onClick?: () => void
}

export const SnackbarProvider: FC<React.PropsWithChildren<unknown>> = ({children}) => {
  const [state, dispatch] = useReducer(reducer, initialState)

  // Stable, so consumers can call them from effects without re-running on every snackbar change
  const closeSnackbar = useCallback(() => dispatch({type: Actions.HIDE}), [])

  const showSnackbar = useCallback(
    (
      text: string,
      autoHide: boolean,
      type?: SnackbarType,
      buttonText?: string,
      onClick?: () => void,
    ) => {
      dispatch({
        type: Actions.SHOW,
        payload: {text, type: type ?? SnackbarType.info, buttonText, onClick},
      })
      autoHide && setTimeout(() => closeSnackbar(), SNACKBAR_AUTO_DISMISS)
    },
    [closeSnackbar],
  )

  return (
    <SnackbarContext.Provider
      value={{
        closeSnackbar,
        showSnackbar,
      }}
    >
      {children}
      {typeof window !== 'undefined' && (
        <Snackbar
          text={state.text}
          type={state.type}
          isVisible={state.isVisible}
          buttonText={state.buttonText}
          onClick={state.onClick}
        />
      )}
    </SnackbarContext.Provider>
  )
}
